import { Color } from '@persona-id/design-tokens';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import PopupBackdrop from 'inquiry/components/PopupBackdrop';
import { useApply } from 'inquiry/lib/jsonLogic';
import { VERIFY_WITH_PERSONA_MESSAGES } from 'inquiry/lib/reusablePersona';
import { setTransitionStart } from 'inquiry/redux/actions/ui';
import { pollInquiry } from 'inquiry/redux/thunk-actions/inquiry';
import EventsClient from 'lib/clients/EventsClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import { ButtonVerifyWithPersonaComponent } from 'lib/interfaces/ui-components';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { fontFamilyCss } from 'verify/lib/utils/font-family';
import { Inquiry, PollingMode } from 'verify/models/inquiry';
import { savePopupUrl } from 'verify/redux/actions/inquiry';
import { RootState } from 'verify/redux/store';

interface ButtonVerifyWithPersonaProps {
  component: ButtonVerifyWithPersonaComponent;
  actionsApi: UiComponentActionsApi;
  inquiry: Inquiry;
  inquiryLoading: boolean;
}

const POPUP_WIDTH = 400;
const POPUP_HEIGHT = 650;
const POPUP_WINDOW_NAME = 'reusable-persona-verify-popup';

const PopupOverlayText = styled.div`
  font-family: ${({ theme }) => fontFamilyCss(theme.fontFamily)};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${Color.PrimaryWhite};
  width: 75%;
  text-align: center;

  a {
    color: ${Color.PrimaryWhite};
    font-weight: bold;
    outline-color: ${Color.PrimaryWhite};
  }
`;

const ButtonVerifyWithPersona = ({
  component,
  actionsApi,
  inquiry,
  inquiryLoading,
}: ButtonVerifyWithPersonaProps) => {
  const { 'button-type': buttonType, disabled, hidden, text } = component.attributes;

  // handle embedded flow popup
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const externalWindowRef = useRef<Window | 'blocked' | null>(null);
  const lastReusablePersonaMessage = useRef<string | null>(null);
  const popupUrl = useSelector<RootState, string | null>((state) => state.inquiry.popupUrl);
  const setPopupUrl = useCallback(
    (popupUrl: string | null) => dispatch(savePopupUrl(popupUrl)),
    [dispatch],
  );
  const [transitioningViaVerifyWithPersonaPopup, setTransitioningViaVerifyWithPersonaPopup] =
    useState(false);

  const {
    error,
    loading: oneTimeCodeLoading,
    callback: openReusablePersonaPopup,
  } = useApiCallback(async () => {
    if (
      popupUrl &&
      externalWindowRef.current &&
      externalWindowRef.current !== 'blocked' &&
      !externalWindowRef.current.closed
    ) {
      externalWindowRef.current.focus();
      return;
    }

    const inquiryResponse = await SessionClientVerify.postResourceAction<Inquiry>(
      {
        id: inquiry.id,
        type: inquiry.type,
      },
      'generate-reusable-persona-verify-popup-one-time-link-code',
    );

    if (inquiryResponse?.meta?.['one-time-link-code']) {
      const url = new URL(component.attributes.url);
      url.searchParams.append('code', inquiryResponse.meta['one-time-link-code']);
      url.searchParams.append('component', component.name);
      // make sure the subdomains match to prevent CORS from rejecting messages
      // generated URL will have customer subdomain but the current location
      // could just be the inquiry.* subdomain
      url.host = window.location.host;

      // triggers popup window to be shown
      setPopupUrl(url.toString());
    }
  }, [popupUrl]);

  useEffect(() => {
    if (popupUrl) {
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2.5;
      lastReusablePersonaMessage.current = null;
      externalWindowRef.current = window.open(
        popupUrl,
        POPUP_WINDOW_NAME,
        `popup,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`,
      );

      // if ref is null, then popup window was blocked
      externalWindowRef.current ||= 'blocked';

      // if window is refocused, check if popup has been dismissed, clean up window ref and url
      const handleRefocus = () => {
        if (
          // if popup window was blocked, assume window was closed and poll if necessary
          externalWindowRef.current === 'blocked' ||
          !externalWindowRef.current ||
          externalWindowRef.current.closed
        ) {
          externalWindowRef.current = null;
          setPopupUrl(null);

          // if popup was closed after a transition request was started but before the main window received a
          // transition polling, completed, or errored event, poll for the inquiry
          if (
            lastReusablePersonaMessage.current === VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_STARTED
          ) {
            dispatch(setTransitionStart());
            // indicates the loading status is due to reusable persona popup
            setTransitioningViaVerifyWithPersonaPopup(true);
            dispatch(pollInquiry(inquiry, { skipInquiryStateUpdateOnPoll: true }));
          }
        }
      };

      window.addEventListener('focus', handleRefocus);

      // update create persona state based on what happens in the popup
      const handlePopupMessage = (e: MessageEvent) => {
        if (
          e.origin === new URL(popupUrl).origin &&
          (e.source === externalWindowRef.current ||
            (externalWindowRef.current === 'blocked' && // if popup was blocked just check originating window name
              (e.source as WindowProxy).name === POPUP_WINDOW_NAME))
        ) {
          if (e.data.name === VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_POLLING) {
            // inquiry has now begun polling inside the popup, start polling for updates here,
            dispatch(setTransitionStart());
            // indicates the loading status is due to reusable persona popup
            setTransitioningViaVerifyWithPersonaPopup(true);
            // override the polling mode of the current inquiry since the RP verify flow will have
            // set it to blocking in the interim. This will prevent transitioning after the first poll,
            // since if inquiry has polling mode none we will always trigger onComplete. This is important
            // for generalized RP flow since transitions happen while the polling mode should still be blocking.
            dispatch(
              pollInquiry(inquiry, {
                pollingMode: PollingMode.BLOCKING,
                skipInquiryStateUpdateOnPoll: true,
              }),
            );
          } else if (e.data.name === VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_COMPLETED) {
            // this case is triggered if the inquiry does not poll after the transition starts
            if (
              lastReusablePersonaMessage.current === VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_STARTED
            ) {
              dispatch(setTransitionStart());
              // indicates the loading status is due to reusable persona popup
              setTransitioningViaVerifyWithPersonaPopup(true);
              // override the polling mode of the current inquiry since the RP verify flow will have
              // set it to blocking in the interim. This will prevent transitioning after the first poll,
              // since if inquiry has polling mode none we will always trigger onComplete. This is important
              // for generalized RP flow since transitions happen while the polling mode should still be blocking.
              dispatch(
                pollInquiry(inquiry, {
                  pollingMode: PollingMode.BLOCKING,
                  skipInquiryStateUpdateOnPoll: true,
                }),
              );
            }
          } else if (e.data.name === VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_ERRORED) {
            dispatch(setTransitionStart());
            // indicates the loading status is due to reusable persona popup
            setTransitioningViaVerifyWithPersonaPopup(true);
            // override the polling mode of the current inquiry since the RP verify flow will have
            // set it to blocking in the interim. This will prevent transitioning after the first poll,
            // since if inquiry has polling mode none we will always trigger onComplete. This is important
            // for generalized RP flow since transitions happen while the polling mode should still be blocking.
            dispatch(
              pollInquiry(inquiry, {
                pollingMode: PollingMode.BLOCKING,
                skipInquiryStateUpdateOnPoll: true,
              }),
            );
          }

          lastReusablePersonaMessage.current = e.data.name;
        }
      };

      window.addEventListener('message', handlePopupMessage);

      // if the main window is closed, close the popup window
      const handleWindowClose = () => {
        if (externalWindowRef.current !== 'blocked') {
          externalWindowRef.current?.close();
        }
      };

      window.addEventListener('close', handleWindowClose);

      return () => {
        window.removeEventListener('focus', handleRefocus);
        window.removeEventListener('message', handlePopupMessage);
        window.removeEventListener('close', handleWindowClose);
      };
    } else {
      if (externalWindowRef.current !== 'blocked') {
        externalWindowRef.current?.close();
      }
      externalWindowRef.current = null;
    }
  }, [popupUrl]);

  useEffect(() => {
    return () => {
      // if we navigate away from the page and this component becomes unmounted, clean up the popup url state which causes the cancel button to be hidden
      setPopupUrl(null);
    };
  }, []);

  useApiErrorHandler({
    error,
    onError: () => actionsApi.showMessage(t('errors.network')),
    onNetworkError: () => actionsApi.showMessage(t('errors.network')),
  });

  useEffect(() => {
    // if inquiry goes from loading to not loading, update the flag so we no longer assume the next
    // inquiry loading status is coming from reusable personas, so we don't show pending status on the button
    if (!inquiryLoading) {
      setTransitioningViaVerifyWithPersonaPopup(false);
    }
  }, [inquiryLoading]);

  const handleEmbedded = useInquiryFeatureFlag('inquiry:enable-reusable-personas-embedded-web');

  const onSubmit = useCallback(
    async (_event: MouseEvent<HTMLButtonElement>) => {
      if (HolderClient.isNonIframeHostedIntegration) {
        EventsClient.logAmplitude('reusable-persona.verify.started-hosted');
        actionsApi.setComponent(component);
        actionsApi.route('/inquiry/verify-with-persona/passkey-authentication');
      } else if (HolderClient.isEmbeddedOrIframeIntegration && handleEmbedded) {
        EventsClient.logAmplitude('reusable-persona.verify.started-embedded');
        await openReusablePersonaPopup();
      }
    },
    [actionsApi, component, openReusablePersonaPopup, handleEmbedded],
  );

  const popupOverlayTextMarkdownOptions = {
    overrides: {
      a: {
        props: {
          target: POPUP_WINDOW_NAME,
          onClick: (event: MouseEvent<HTMLElement>) => {
            // if popupURL was blocked and the popupURL is present, don't
            // prevent the default click event since the link should now be popupURL
            if (externalWindowRef.current !== 'blocked' || !popupUrl) {
              event.preventDefault();
              openReusablePersonaPopup();
            }
          },
        },
      },
    },
  };

  const apply = useApply(component.name ?? '', inquiry.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);
  const isWebAuthnSupported = browserSupportsWebAuthn();

  const shouldRender = useMemo(() => {
    if (isHidden) {
      return false;
    }

    if (!isWebAuthnSupported) {
      return false;
    }

    if (HolderClient.isEmbeddedOrIframeIntegration) {
      return handleEmbedded;
    }

    return HolderClient.isNonIframeHostedIntegration;
  }, [isHidden, handleEmbedded]);

  useEffect(() => {
    if (shouldRender) {
      EventsClient.logAmplitude('reusable-persona.verify.btn-rendered');
    }
  }, []);

  if (!shouldRender) {
    return null;
  }

  const isDisabled =
    inquiryLoading ||
    oneTimeCodeLoading ||
    apply<boolean>(disabled) ||
    !!inquiry.attributes['reusable-persona-status'];

  return (
    <>
      <PopupBackdrop visible={!!popupUrl} onClose={() => setPopupUrl(null)}>
        <PopupOverlayText id='verify_with_persona__popup_overlay_text'>
          <Markdown markdownOptions={popupOverlayTextMarkdownOptions}>
            {
              /*
                set popupURL as the link URL so that if the popup is blocked,
                it is just a regular link on the overlay and doesn't use window.open()
                */
              component.attributes['popup-overlay-text'].replace('(#)', `(${popupUrl})`)
            }
          </Markdown>
        </PopupOverlayText>
      </PopupBackdrop>
      <Button
        id='verify_with_persona__button'
        buttonType={buttonType ?? ButtonType.Primary}
        onClick={onSubmit}
        disabled={isDisabled}
        styles={component?.styles}
        isPending={(inquiryLoading && transitioningViaVerifyWithPersonaPopup) || oneTimeCodeLoading}
      >
        {text}
      </Button>
    </>
  );
};

export default ButtonVerifyWithPersona;
